import { type FC, type SVGProps } from 'react';

import styles from './LinkIcons.module.css';

const DEFAULT_SIZE = 18;
const FIGMA_ASPECT_RATIO = 0.67;

interface IconProps extends SVGProps<SVGSVGElement> {
	size?: number;
}

/**
 * Figma official logotype - the iconic 5-piece stacked design
 */
export const FigmaIcon: FC<IconProps> = ({ size = DEFAULT_SIZE, className, ...props }) => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 38 57'
		width={size * FIGMA_ASPECT_RATIO}
		height={size}
		fill='none'
		className={`${styles.icon} ${styles.figma} ${className || ''}`}
		{...props}
	>
		{/* Top-left: orange-red */}
		<path
			className={styles.figmaPiece}
			d='M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38H19V28.5Z'
			fill='currentColor'
			opacity='0.9'
		/>
		{/* Top-right: purple-pink */}
		<path
			className={styles.figmaPiece}
			d='M0 9.5C0 4.25329 4.25329 0 9.5 0H19V19H9.5C4.25329 19 0 14.7467 0 9.5Z'
			fill='currentColor'
			opacity='0.6'
		/>
		{/* Middle-left: purple */}
		<path
			className={styles.figmaPiece}
			d='M19 0H28.5C33.7467 0 38 4.25329 38 9.5C38 14.7467 33.7467 19 28.5 19H19V0Z'
			fill='currentColor'
			opacity='0.75'
		/>
		{/* Middle-right: blue */}
		<path
			className={styles.figmaPiece}
			d='M0 28.5C0 23.2533 4.25329 19 9.5 19H19V38H9.5C4.25329 38 0 33.7467 0 28.5Z'
			fill='currentColor'
			opacity='0.5'
		/>
		{/* Bottom: green */}
		<path
			className={styles.figmaPiece}
			d='M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z'
			fill='currentColor'
			opacity='0.35'
		/>
	</svg>
);

/**
 * Hexagonal plugin icon with circuit-inspired inner pattern
 */
export const PluginIcon: FC<IconProps> = ({ size = DEFAULT_SIZE, className, ...props }) => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 24 24'
		width={size}
		height={size}
		fill='none'
		className={`${styles.icon} ${styles.plugin} ${className || ''}`}
		{...props}
	>
		<path
			className={styles.stroke}
			d='M12 2L21.66 7V17L12 22L2.34 17V7L12 2Z'
			strokeWidth='1.5'
			strokeLinejoin='round'
		/>
		<path
			className={styles.strokeSubtle}
			d='M12 6L17.2 9V15L12 18L6.8 15V9L12 6Z'
			strokeWidth='1'
			strokeLinejoin='round'
		/>
		<circle className={styles.fillAccent} cx='12' cy='12' r='2.5' />
		<path
			className={styles.strokeAccent}
			d='M12 9.5V6M14.17 10.75L17.2 9M14.17 13.25L17.2 15M12 14.5V18M9.83 13.25L6.8 15M9.83 10.75L6.8 9'
			strokeWidth='1'
			strokeLinecap='round'
		/>
	</svg>
);

/**
 * GitHub stylized mark - octocat silhouette essence
 */
export const GitHubIcon: FC<IconProps> = ({ size = DEFAULT_SIZE, className, ...props }) => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		viewBox='0 0 24 24'
		width={size}
		height={size}
		fill='none'
		className={`${styles.icon} ${styles.github} ${className || ''}`}
		{...props}
	>
		<path
			className={styles.fill}
			d='M12 2C6.477 2 2 6.477 2 12C2 16.418 4.865 20.166 8.839 21.489C9.339 21.589 9.521 21.273 9.521 21.007C9.521 20.773 9.505 19.973 9.505 19.139C7 19.657 6.35 18.543 6.15 17.957C6.037 17.657 5.55 16.773 5.125 16.539C4.775 16.357 4.275 15.839 5.112 15.827C5.9 15.814 6.462 16.557 6.65 16.873C7.55 18.414 8.988 17.973 9.559 17.707C9.65 17.039 9.912 16.586 10.2 16.327C7.975 16.068 5.65 15.206 5.65 11.393C5.65 10.293 6.037 9.381 6.675 8.657C6.575 8.398 6.225 7.348 6.775 5.948C6.775 5.948 7.612 5.677 9.521 6.989C10.321 6.757 11.175 6.641 12.021 6.641C12.871 6.641 13.721 6.757 14.521 6.989C16.429 5.664 17.266 5.948 17.266 5.948C17.816 7.348 17.466 8.398 17.366 8.657C18.004 9.381 18.391 10.281 18.391 11.393C18.391 15.219 16.054 16.068 13.829 16.327C14.191 16.639 14.504 17.239 14.504 18.164C14.504 19.489 14.492 20.556 14.492 21.007C14.492 21.273 14.675 21.602 15.175 21.489C17.153 20.828 18.869 19.563 20.088 17.873C21.306 16.183 21.971 14.149 21.971 12.06C22 6.477 17.523 2 12 2Z'
		/>
		<ellipse className={styles.eyeHighlight} cx='8.5' cy='10' rx='0.75' ry='1' />
		<ellipse className={styles.eyeHighlight} cx='15.5' cy='10' rx='0.75' ry='1' />
	</svg>
);
